import { useState } from "react";
import {
  Code2,
  Cpu,
  Layers,
  Search,
  CheckCircle2,
  AlertCircle,
  Clock,
  ShieldCheck,
  Send,
  Loader2,
  Check,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

import Container from "../../Components/Container/Container";
import Seo from "../../Components/SEO/Seo";
import Button from "../../Components/Button/Button";

const DEVELOPER_ROLES = [
  {
    type: "Frontend Developer",
    scope: "Website & app user interfaces, interactive SPA platforms, performance optimization",
    tech: ["HTML", "CSS", "JavaScript", "React", "Vue", "Angular"],
    category: "Frontend",
  },
  {
    type: "Backend Developer",
    scope: "Servers, scalable APIs, database schema design, core business logic",
    tech: ["Node.js", "Python", "Java", "PHP", ".NET"],
    category: "Backend",
  },
  {
    type: "Full-Stack Developer",
    scope: "End-to-end development handling responsive frontend and robust backend workflows",
    tech: ["React", "Node.js", "MERN", "Java/Spring", "Next.js"],
    category: "Full-Stack",
  },
  {
    type: "Mobile App Developer",
    scope: "Native & cross-platform Android and iOS mobile applications",
    tech: ["Kotlin", "Java", "Swift", "Flutter", "React Native"],
    category: "Mobile",
  },
  {
    type: "Desktop Developer",
    scope: "High-performance cross-platform Windows, macOS, and Linux software",
    tech: ["C#", "C++", "Java", "Electron"],
    category: "Desktop",
  },
  {
    type: "Game Developer",
    scope: "Interactive 2D/3D gameplay mechanics, physics engines, asset pipelines",
    tech: ["Unity", "Unreal Engine", "C#", "C++"],
    category: "Specialized",
  },
  {
    type: "DevOps Engineer / Developer",
    scope: "Automated deployment, containerization, cloud infrastructure, CI/CD pipelines",
    tech: ["Docker", "Kubernetes", "AWS", "Azure", "GitHub Actions"],
    category: "DevOps & Cloud",
  },
  {
    type: "Cloud Developer",
    scope: "Serverless architectures, cloud-native services, microservice integrations",
    tech: ["AWS", "Azure", "GCP"],
    category: "DevOps & Cloud",
  },
  {
    type: "Software Engineer",
    scope: "Distributed architectures, microservices, enterprise-grade software platforms",
    tech: ["Java", "C++", "Python", "Go"],
    category: "Core",
  },
  {
    type: "Embedded Systems Developer",
    scope: "Low-level firmware, microcontrollers, IoT devices, hardware communication",
    tech: ["C", "C++", "Rust"],
    category: "Specialized",
  },
  {
    type: "AI / ML Developer",
    scope: "Machine learning model pipelines, predictive AI, NLP, computer vision applications",
    tech: ["Python", "PyTorch", "TensorFlow"],
    category: "AI & Data",
  },
  {
    type: "Data Engineer",
    scope: "Big data ETL pipelines, data warehouses, streaming systems, lakehouse architectures",
    tech: ["Python", "SQL", "Spark", "Kafka"],
    category: "AI & Data",
  },
  {
    type: "Cybersecurity Developer",
    scope: "Security tooling, automated vulnerability scanners, cryptography, hardened logic",
    tech: ["Python", "C/C++", "Java", "Security Frameworks"],
    category: "Security",
  },
  {
    type: "Blockchain Developer",
    scope: "Smart contracts, decentralized Web3 protocols, dApps, cryptographic validation",
    tech: ["Solidity", "Rust", "JavaScript"],
    category: "Specialized",
  },
  {
    type: "QA / Automation Developer",
    scope: "Automated regression testing, E2E browser tests, API quality verification",
    tech: ["Selenium", "Playwright", "Cypress", "Python", "Java"],
    category: "QA",
  },
];

const ALL_TECH_OPTIONS = Array.from(
  new Set(DEVELOPER_ROLES.flatMap((r) => r.tech))
).sort();

export default function HireTalent() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState(DEVELOPER_ROLES[0].type);
  const [selectedTechs, setSelectedTechs] = useState(DEVELOPER_ROLES[0].tech);
  const [showAllTechs, setShowAllTechs] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    engagementType: "Full-Time Dedicated",
    details: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // { type: 'success' | 'error', message: '' }

  const filteredRoles = DEVELOPER_ROLES.filter(
    (item) =>
      item.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.scope.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tech.some((t) => t.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const toggleTech = (tech) => {
    setSelectedTechs((prev) =>
      prev.includes(tech) ? prev.filter((t) => t !== tech) : [...prev, tech]
    );
  };

  const handleSelectRole = (role) => {
    setSelectedRole(role.type);
    setSelectedTechs(role.tech);
    const formElement = document.getElementById("hire-intake-form");
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleRoleChange = (newRoleType) => {
    setSelectedRole(newRoleType);
    const matched = DEVELOPER_ROLES.find((r) => r.type === newRoleType);
    if (matched) {
      setSelectedTechs(matched.tech);
    }
  };

  const handleSelectAllRoleTechs = () => {
    const matched = DEVELOPER_ROLES.find((r) => r.type === selectedRole);
    if (matched) {
      setSelectedTechs(Array.from(new Set([...selectedTechs, ...matched.tech])));
    }
  };

  const handleClearAllTechs = () => {
    setSelectedTechs([]);
  };

  // Asynchronous AJAX call to send-staffing-request.php
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const payload = {
      name: formData.name,
      email: formData.email,
      company: formData.company,
      role: selectedRole,
      technologies: selectedTechs,
      engagementType: formData.engagementType,
      details: formData.details,
    };

    try {
      const response = await fetch("/api/send-staffing-request.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && result.status === "success") {
        setSubmitStatus({
          type: "success",
          message: result.message || "Thank you! Your staffing request has been submitted successfully.",
        });
        setFormData({
          name: "",
          email: "",
          company: "",
          engagementType: "Full-Time Dedicated",
          details: "",
        });
      } else {
        setSubmitStatus({
          type: "error",
          message: result.message || "Unable to send your request. Please try again.",
        });
      }
    } catch (err) {
      setSubmitStatus({
        type: "error",
        message: "Failed to connect to the mail server. Please ensure the PHP script is running.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayedTechs = showAllTechs
    ? ALL_TECH_OPTIONS
    : Array.from(
        new Set([
          ...selectedTechs,
          ...(DEVELOPER_ROLES.find((r) => r.type === selectedRole)?.tech || []),
          "JavaScript",
          "React",
          "Node.js",
          "Python",
          "Docker",
          "AWS",
        ])
      );

  return (
    <div className="legal-page hire-talent-page">
      <Seo
        title="Hire Dedicated Developers & Software Engineers | Conectify Tech"
        description="Hire vetted frontend, backend, full-stack, mobile, cloud, AI/ML, and DevOps software developers. Choose individual technologies or complete stacks."
      />

      <Container className="legal-container">
        {/* Header */}
        <header className="legal-header hire-talent__header">
          <span className="section-eyebrow eyes">
            <Code2 size={14} className="inline mr-1" /> On-Demand Engineering Staffing
          </span>
          <h1 className="legal-header__title">Hire Dedicated Developers</h1>
          <p className="legal-header__date">
            Access top software engineering talent across 15+ specializations. Vetted professionals ready to integrate directly into your sprint cycles.
          </p>
        </header>

        {/* 3-Column Highlights */}
        <div className="hire-features-grid">
          <div className="hire-feature-card">
            <div className="hire-feature-card__icon-wrap">
              <Cpu size={22} />
            </div>
            <h3>Technical Vetting</h3>
            <p>
              Every engineer undergoes rigorous algorithm, architecture, and live coding evaluations before client placement.
            </p>
          </div>

          <div className="hire-feature-card">
            <div className="hire-feature-card__icon-wrap">
              <Clock size={22} />
            </div>
            <h3>Fast 72-Hour Matching</h3>
            <p>
              Receive candidate profiles aligned with your tech stack, seniority requirements, and time zone within 3 business days.
            </p>
          </div>

          <div className="hire-feature-card">
            <div className="hire-feature-card__icon-wrap">
              <ShieldCheck size={22} />
            </div>
            <h3>Trial Period &amp; Replacement</h3>
            <p>
              Zero-risk engagement with a two-week trial period and free replacements to protect your product roadmap.
            </p>
          </div>
        </div>

        {/* Directory & Filter */}
        <div className="hire-directory-section">
          <div className="hire-directory-header">
            <div>
              <h2 className="hire-directory-title">
                Developer Specializations &amp; Tech Stacks
              </h2>
              <p className="hire-directory-sub">
                Select a developer type to pre-fill your hiring request form below.
              </p>
            </div>

            <div className="hire-search-bar">
              <Search size={16} className="hire-search-icon" />
              <input
                type="text"
                placeholder="Search skills, technologies, or roles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="hire-search-input"
              />
            </div>
          </div>

          {/* Desktop Table */}
          <div className="legal-table-wrapper hire-desktop-table">
            <div className="legal-table-scroll">
              <table className="gdpr-table">
                <thead>
                  <tr>
                    <th style={{ width: "22%" }}>Developer Type</th>
                    <th style={{ width: "40%" }}>What They Build / Work On</th>
                    <th style={{ width: "26%" }}>Common Technologies</th>
                    <th style={{ width: "12%", textAlign: "center" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRoles.length > 0 ? (
                    filteredRoles.map((role) => (
                      <tr key={role.type}>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Layers size={16} style={{ color: "var(--primary, #0284c7)" }} />
                            <strong style={{ fontSize: "0.925rem" }}>{role.type}</strong>
                          </div>
                        </td>
                        <td style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
                          {role.scope}
                        </td>
                        <td>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                            {role.tech.map((skill) => (
                              <span
                                key={skill}
                                className="legal-badge legal-badge--blue"
                                style={{ fontSize: "0.725rem", padding: "2px 7px" }}
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <button
                            type="button"
                            onClick={() => handleSelectRole(role)}
                            className={`hire-action-btn ${
                              selectedRole === role.type ? "hire-action-btn--selected" : ""
                            }`}
                          >
                            {selectedRole === role.type ? "Selected" : "Hire This"}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="gdpr-table__empty">
                        No developer roles matched "{searchTerm}".
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="hire-mobile-cards">
            {filteredRoles.length > 0 ? (
              filteredRoles.map((role) => {
                const isSelected = selectedRole === role.type;
                return (
                  <div
                    key={role.type}
                    className={`hire-mobile-card ${isSelected ? "hire-mobile-card--selected" : ""}`}
                  >
                    <div className="hire-mobile-card__header">
                      <div className="hire-mobile-card__title">
                        <Layers size={18} className="text-primary" />
                        <span>{role.type}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleSelectRole(role)}
                        className={`hire-action-btn ${
                          isSelected ? "hire-action-btn--selected" : ""
                        }`}
                      >
                        {isSelected ? "Selected" : "Hire"}
                      </button>
                    </div>

                    <p className="hire-mobile-card__scope">{role.scope}</p>

                    <div className="hire-mobile-card__tech">
                      {role.tech.map((skill) => (
                        <span key={skill} className="legal-badge legal-badge--blue">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="hire-empty-mobile">No developer roles matched "{searchTerm}".</div>
            )}
          </div>
        </div>

        {/* Intake Request Form */}
        <article id="hire-intake-form" className="legal-card legal-content hire-form-card">
          <div className="hire-form-header">
            <span className="section-eyebrow eyes">Direct Staffing Request</span>
            <h2>Build Your Engineering Team</h2>
            <p>
              Submit your talent requirements. Our technical recruiter will contact you within 24 hours with pre-screened candidate options.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="hire-form">
            <div className="hire-form-grid">
              <div>
                <label className="hire-form-label">Your Full Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Alex Johnson"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="hire-form-input"
                />
              </div>

              <div>
                <label className="hire-form-label">Work Email *</label>
                <input
                  type="email"
                  required
                  placeholder="name@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="hire-form-input"
                />
              </div>
            </div>

            <div className="hire-form-grid">
              <div>
                <label className="hire-form-label">Company Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. TechCorp Solutions"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="hire-form-input"
                />
              </div>

              <div>
                <label className="hire-form-label">Developer Specialization Needed</label>
                <select
                  value={selectedRole}
                  onChange={(e) => handleRoleChange(e.target.value)}
                  className="hire-form-select"
                >
                  {DEVELOPER_ROLES.map((role) => (
                    <option key={role.type} value={role.type}>
                      {role.type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Interactive Tech Selector */}
            <div className="hire-tech-box">
              <div className="hire-tech-box__header">
                <label className="hire-tech-box__label">
                  Select Technologies ({selectedTechs.length} selected):
                </label>
                <div className="hire-tech-box__actions">
                  <button
                    type="button"
                    onClick={handleSelectAllRoleTechs}
                    className="hire-tech-link"
                  >
                    Role Defaults
                  </button>
                  <span className="hire-tech-divider">|</span>
                  <button
                    type="button"
                    onClick={handleClearAllTechs}
                    className="hire-tech-link hire-tech-link--muted"
                  >
                    Clear All
                  </button>
                </div>
              </div>

              <div className="hire-tech-chips">
                {displayedTechs.map((tech) => {
                  const isChecked = selectedTechs.includes(tech);
                  return (
                    <button
                      key={tech}
                      type="button"
                      onClick={() => toggleTech(tech)}
                      className={`hire-chip ${isChecked ? "hire-chip--checked" : ""}`}
                    >
                      {isChecked && <Check size={12} className="hire-chip__check" />}
                      <span>{tech}</span>
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => setShowAllTechs(!showAllTechs)}
                className="hire-show-all-btn"
              >
                {showAllTechs ? (
                  <>
                    <ChevronUp size={14} /> Show Less Technologies
                  </>
                ) : (
                  <>
                    <ChevronDown size={14} /> Browse All {ALL_TECH_OPTIONS.length} Technologies
                  </>
                )}
              </button>
            </div>

            {/* Engagement Model */}
            <div>
              <label className="hire-form-label">Engagement Model</label>
              <select
                value={formData.engagementType}
                onChange={(e) => setFormData({ ...formData, engagementType: e.target.value })}
                className="hire-form-select"
              >
                <option value="Full-Time Dedicated">Full-Time Dedicated (40 hrs/week)</option>
                <option value="Part-Time / Staff Augmentation">Part-Time / Staff Augmentation (20 hrs/week)</option>
                <option value="Project-Based Contract">Project-Based Sprint Milestone</option>
                <option value="Direct Hire / Permanent Placement">Direct Hire / Full-Time Employee Placement</option>
              </select>
            </div>

            {/* Project Details */}
            <div>
              <label className="hire-form-label">Seniority Level &amp; Project Description</label>
              <textarea
                rows={4}
                placeholder="Mention seniority (Junior, Mid, Senior, Lead), required project deliverables, sprint timelines, or timezone requirements..."
                value={formData.details}
                onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                className="hire-form-textarea"
              />
            </div>

            <Button
              type="submit"
              block
              disabled={isSubmitting}
              icon={isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            >
              {isSubmitting ? "Submitting Staffing Request..." : "Submit Hiring Request"}
            </Button>

            {/* Success / Error Message Banner */}
            {submitStatus && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "14px",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  background:
                    submitStatus.type === "success"
                      ? "rgba(16, 185, 129, 0.1)"
                      : "rgba(239, 68, 68, 0.1)",
                  color: submitStatus.type === "success" ? "#059669" : "#dc2626",
                  border: `1px solid ${
                    submitStatus.type === "success"
                      ? "rgba(16, 185, 129, 0.3)"
                      : "rgba(239, 68, 68, 0.3)"
                  }`,
                }}
              >
                {submitStatus.type === "success" ? (
                  <CheckCircle2 size={18} />
                ) : (
                  <AlertCircle size={18} />
                )}
                <span>{submitStatus.message}</span>
              </div>
            )}
          </form>
        </article>
      </Container>
    </div>
  );
}